import { serializeMediaReference, type VariantCount } from '@air/feature-ag-ui-chat';
import { getSizeDimensionsLabel } from '@air/feature-saved-sizes/utils';

import {
  getOutputDimensionsForFormat,
  resolveExportDimensionsForFormat,
} from '../nodes/Image/smartResize/utils/outputDimensions';
import { type SmartResizeFormat } from '../nodes/Image/smartResizeShared';
import { FULL_CIRCLE, ZOOM_STEP_TO_MULTIPLIER } from '../nodes/Image/transforms/cameraAngle/constants';
import { type CameraMotionResolution } from '../nodes/Image/transforms/cameraMotion/utils/cameraMotionConfig';
import { toCameraMotionTrajectory } from '../nodes/Image/transforms/cameraMotion/utils/cameraMotionKeyframes';
import { type CameraMotionKeyframe } from '../nodes/Image/transforms/cameraMotion/utils/cameraMotionTypes';
import {
  type BrightnessId,
  type ConeWidthId,
  type DistanceId,
  type EmissionId,
  type LightTypeId,
  type SunTimeOfDayId,
} from '../nodes/Image/transforms/changeLighting/constants';
import { buildLightingPrompt } from '../nodes/Image/transforms/changeLighting/prompt';
import { type ExtendVideoDuration } from '../nodes/Video/transforms/extend/extendVideoDuration';
import { type BoundingBox } from '../types';

export type BaseImageParams = {
  nodeId: string;
  height: number;
  width: number;
};

export type TransformMessageParams =
  | {
      nodeId: string;
      rotation: number;
      tilt: number;
      zoom: number;
      type: 'cameraAngle';
    }
  | {
      duration: number;
      keyframes: readonly CameraMotionKeyframe[];
      nodeId: string;
      resolution: CameraMotionResolution;
      type: 'cameraMotion';
    }
  | (BaseImageParams & {
      azimuth: number;
      brightness: BrightnessId;
      coneWidth?: ConeWidthId;
      distance: DistanceId;
      elevation: number;
      emissionId: EmissionId;
      lightTypeId: LightTypeId;
      solidColor?: string;
      sunTimeOfDay?: SunTimeOfDayId;
      type: 'changeLighting';
    })
  | (BaseImageParams & { boundingBox: BoundingBox<'pixel'>; type: 'crop' })
  | (BaseImageParams & { boundingBox: BoundingBox<'pixel'>; type: 'removeObject' })
  | (BaseImageParams & { factor: number; type: 'upscale' })
  | (BaseImageParams & {
      extend: { bottom: number; left: number; right: number; top: number };
      type: 'extendBackground';
    })
  | (BaseImageParams & { type: 'editText' })
  | (BaseImageParams & { remoteNodeId?: string; type: 'editTextOcr' | 'localizationOcr' })
  | (BaseImageParams & {
      edits: Array<{
        boundingBox: { height: number; width: number; x: number; y: number };
        newText: string;
        text: string;
      }>;
      imageUrl: string;
      type: 'replaceTextOcr';
    })
  | (BaseImageParams & {
      imageUrl: string;
      protectedRegions: Array<{ boundingBox: { height: number; width: number; x: number; y: number } }>;
      regions: Array<{
        boundingBox: { height: number; width: number; x: number; y: number };
        originalText: string;
        translatedText?: string;
      }>;
      remoteNodeId?: string;
      targetLanguage: string;
      textDirection: 'ltr' | 'rtl';
      type: 'translate';
    })
  // Deliberately has no `nodeId` — this is a text-only translation request, so it must not carry
  // the trailing "source media reference" line `buildTransformMessage` appends for image params,
  // and must not give the agent anything to run OCR against.
  | {
      /** `context` is the parent line a span was cut from, for disambiguation only — never part of the translation. */
      strings: Array<{ context: string; id: string; text: string }>;
      targetLanguage: string;
      textDirection: 'ltr' | 'rtl';
      type: 'localizationTranslate';
    }
  | (BaseImageParams & { type: 'extractLayers' })
  | { color?: string; nodeId: string; type: 'removeBackground' }
  | { height: number; prompt: string; type: 'generateImage'; width: number }
  | { duration?: number; nodeId: string; type: 'videoToGif' }
  | {
      formats: Array<
        { format: string; formatId: string; platform: string } & (
          | {
              /** Must be one of `reframe_video`'s supported ratios, e.g. "9:16". */
              aspectRatio: string;
            }
          | { height: number; width: number }
        )
      >;
      nodeId: string;
      type: 'videoSmartResize';
    }
  | { nodeId: string; type: 'imageToVector' }
  | { nodeId: string; prompt: string; type: 'imageToVideo'; variantCount: VariantCount | undefined };

// Convert azimuth from signed degrees (−180..180) to unsigned (0..360) for the backend tool
const toUnsignedAzimuth = (degrees: number): number => ((degrees % FULL_CIRCLE) + FULL_CIRCLE) % FULL_CIRCLE;

/** fal cameraControl scene prompt — trajectory carries the path; avoid scene-action verbs that animate subjects. */
const CAMERA_MOTION_FROZEN_SCENE_PROMPT = [
  'The reference image is one frozen instant. Only the camera moves.',
  'Orbit around the subject in the reference image and follow camera_trajectory exactly, interpolating smoothly between keyframes.',
  'Do not pan across a flat image or slide the background independently of the subject.',
  'Preserve every person and object in exactly the same world position, orientation, shape and pose throughout the shot.',
  'Airborne objects remain suspended at the captured height and angle: no wobbling, shaking, spinning, drifting, falling or continued action.',
  'Keep faces, hands, clothing, liquids and the background motionless while retaining their natural appearance.',
  'Camera parallax is the only source of apparent movement.',
  'Follow the supplied camera path smoothly, easing into and out of the move.',
  'Keep unchanged lighting and focal length.',
  'No cuts, zoom, morphing or added objects.',
].join(' ');

const getTransformationMessage = (params: TransformMessageParams): string[] => {
  switch (params.type) {
    case 'cameraAngle': {
      const azimuth = toUnsignedAzimuth(params.rotation);
      const elevation = params.tilt;
      const zoomMultiplier = ZOOM_STEP_TO_MULTIPLIER[params.zoom];

      const perspectiveParams = [
        `- azimuth: ${azimuth}`,
        `- elevation: ${elevation}`,
        ...(zoomMultiplier !== undefined ? [`- zoom: ${zoomMultiplier}`] : []),
      ];

      return [
        `Load the \`ai-editing\` skill, then use \`change_perspective\` on ${serializeMediaReference(params.nodeId)} with these parameters:`,
        ...perspectiveParams,
      ];
    }

    case 'cameraMotion': {
      const cameraTrajectory = JSON.stringify(toCameraMotionTrajectory(params.keyframes));

      return [
        `Load the \`ai-editing\` skill, then use \`ai_video\` with the "cameraControl" operation on ${serializeMediaReference(params.nodeId)}.`,
        `- camera_trajectory: ${cameraTrajectory}`,
        `- duration: ${params.duration}`,
        `- resolution: ${params.resolution}`,
        `- Prompt: "${CAMERA_MOTION_FROZEN_SCENE_PROMPT}"`,
        `Keep the scene frozen; only the camera moves along the trajectory. Preserve the source image's aspect ratio in the generated video.`,
      ];
    }

    case 'changeLighting': {
      const lightingPrompt = buildLightingPrompt({
        azimuth: params.azimuth,
        brightness: params.brightness,
        coneWidth: params.coneWidth,
        distance: params.distance,
        elevation: params.elevation,
        emissionId: params.emissionId,
        lightTypeId: params.lightTypeId,
        solidColor: params.solidColor,
        sunTimeOfDay: params.sunTimeOfDay,
      });

      return [
        `Load the \`ai-editing\` skill, then use \`ai_image\` with the "edit" operation on ${serializeMediaReference(params.nodeId)}.`,
        `- Prompt: "${lightingPrompt}"`,
        `- Source image dimensions: ${params.width}×${params.height}. Preserve these dimensions and aspect ratio as closely as the selected model allows. If the model exposes a resolution, size, or output-dimensions parameter, pick the option that lands closest to the source — do not upscale to a larger bucket or downsize to a smaller one. Do not chain a separate upscaling, super-resolution, or resampling step on the result; those mangle fine detail (text, edges, faces).`,
      ];
    }

    case 'crop':
      return [
        `Crop the image ${serializeMediaReference(params.nodeId)}. Here are the details:`,
        `- Source image size: ${params.width}x${params.height}`,
        `- Crop bounding box: left=${params.boundingBox.x}, top=${params.boundingBox.y}, width=${params.boundingBox.width}, height=${params.boundingBox.height}`,
      ];

    case 'removeObject':
      return [
        `Use \`erase_region\` on ${serializeMediaReference(params.nodeId)} with a bounding box region:`,
        // `erase_region` reads the region in image space, so it can only be trusted against the
        // source image's own size — emit it the way the crop branch does.
        `- Source image size: ${params.width}x${params.height}`,
        `- regions: [{ x: ${params.boundingBox.x}, y: ${params.boundingBox.y}, width: ${params.boundingBox.width}, height: ${params.boundingBox.height} }]`,
      ];

    case 'upscale':
      return [
        `Load the \`ai-editing\` skill, then use \`ai_image\` with the "upscale" operation with mode "factor" on ${serializeMediaReference(params.nodeId)}.`,
        `- factor: ${params.factor}`,
      ];

    case 'extendBackground':
      return [
        `Use \`outpaint_image\` to expand ${serializeMediaReference(params.nodeId)} to the target aspect ratio with matching content (do not crop or pad — preserve all content):`,
        `- canvasWidth: ${params.width + params.extend.left + params.extend.right}`,
        `- canvasHeight: ${params.height + params.extend.top + params.extend.bottom}`,
        `- originalImageSize: [${params.width}, ${params.height}]`,
        `- originalImagePosition: [${params.extend.left}, ${params.extend.top}]`,
      ];

    case 'editText':
      return [
        `Use \`extract_editable_text\` on ${serializeMediaReference(params.nodeId)}.`,
        `After extraction, upload the clean image via \`upload_media\`, then call \`file_edit_complete\` with all layers — the clean image (with its group) and each text layer (with type "text", textNode, and group).`,
        `Use the groupId and group data from the \`extract_editable_text\` result directly.`,
      ];

    case 'editTextOcr':
    case 'localizationOcr':
      return [
        `Call the \`detect_text\` tool with the following parameters:`,
        `- imageUrl: ${serializeMediaReference(params.remoteNodeId ?? params.nodeId)}`,
        `- maxRegions: 100`,
        `- includeWords: true`,
        // A media reference only resolves once that media is loaded, which it may not be yet — the tool answers NOT_FOUND
        // until then. A blanket "no other tools" ban would forbid the agent from recovering, leaving the caller waiting on a
        // result that was never coming.
        `If \`detect_text\` fails with NOT_FOUND, the image is not loaded yet: call \`load_media\` on that same source, then call \`detect_text\` again with the \`filePath\` it returns.`,
        `Use no tools other than \`load_tools\`, \`load_media\`, and \`detect_text\`. Return the detect_text result directly.`,
      ];

    case 'translate': {
      const editsJson = JSON.stringify(
        params.regions.map((region) => ({
          boundingBox: region.boundingBox,
          text: region.originalText,
          ...(region.translatedText ? { newText: region.translatedText } : {}),
        })),
      );
      const protectedJson = JSON.stringify(params.protectedRegions.map((region) => region.boundingBox));

      return [
        `Localize the text in ${serializeMediaReference(params.remoteNodeId ?? params.nodeId)} into ${params.targetLanguage}.`,
        `Translate each region below into ${params.targetLanguage} (${params.textDirection === 'rtl' ? 'right-to-left script' : 'left-to-right script'}). When a region already includes "newText", use that exact translation instead of translating it yourself.`,
        // Without this the run's chat narration often switches to the target language: the request
        // names it twice and the regions below carry the user's own translations, which reads as a
        // language preference rather than as the content to edit.
        `Only the text inside the image changes language — write your own replies in English.`,
        `Regions: ${editsJson}`,
        `Then call the \`replace_image_text\` tool with:`,
        `- imageUrl: "${params.imageUrl}"`,
        `- edits: the same regions, each with its translated text as "newText".`,
        params.protectedRegions.length > 0
          ? `Do not modify text inside these protected areas (image-pixel bounding boxes) or any logos/brand marks: ${protectedJson}.`
          : `Do not modify logos, brand marks, or any text not listed above.`,
        `After \`replace_image_text\` completes, call \`upload_media\` with the resulting file path, then \`file_edit_complete\` with the uploaded URL to deliver the localized image.`,
      ];
    }

    case 'localizationTranslate': {
      const stringsJson = JSON.stringify(params.strings);
      const directionLabel = params.textDirection === 'rtl' ? 'right-to-left script' : 'left-to-right script';

      return [
        `Translate the following text strings into ${params.targetLanguage} (${directionLabel}).`,
        `Strings: ${stringsJson}`,
        `Each string can be a fragment of a longer line — "context" is that full line, given only so you can disambiguate the fragment's meaning. Translate "text" alone; never fold "context" into the translation.`,
        `For every string, produce its translation, then load the \`submit_translations\` tool and call it exactly once with targetLanguage "${params.targetLanguage}" and a "translations" array containing one entry per string above — each with its "id" echoed back exactly as given, its "originalText", and your "translatedText".`,
        `Return an entry for every string listed — none may be dropped.`,
        `Leave brand names, product names, and trademarks untranslated.`,
        `Use no tools other than \`load_tools\` and \`submit_translations\`, and do not modify any image.`,
      ];
    }

    case 'replaceTextOcr': {
      const editsJson = JSON.stringify(params.edits);

      return [
        `Call the \`replace_image_text\` tool with the following parameters:`,
        `- imageUrl: "${params.imageUrl}"`,
        `- edits: ${editsJson}`,
        `After \`replace_image_text\` completes, call \`upload_media\` with the resulting file path.`,
        `Then call \`file_edit_complete\` with the uploaded URL to deliver the result.`,
      ];
    }

    case 'extractLayers':
      return [
        `Load the \`decompose-image\` skill, then split ${serializeMediaReference(params.nodeId)} into individual foreground elements with transparent backgrounds plus a clean background.`,
        `Deliver the result as a grouped composition for independent editing.`,
      ];

    case 'removeBackground':
      return params.color
        ? [
            `Load the \`ai-editing\` skill, then use \`ai_image\` with the "removeBackground" operation on ${serializeMediaReference(params.nodeId)}.`,
            `After removing the background, composite the result onto a solid "${params.color}" canvas using \`composite_images\` to produce the final output.`,
          ]
        : [
            `Load the \`ai-editing\` skill, then use \`ai_image\` with the "removeBackground" operation on ${serializeMediaReference(params.nodeId)}.`,
          ];

    case 'generateImage':
      return [
        `Load the \`ai-editing\` skill, then use \`ai_image\` with the "generate" operation.`,
        `- Prompt: "${params.prompt}"`,
        `- Target dimensions: ${params.width}x${params.height}`,
      ];

    case 'videoToGif':
      return [
        `Use \`video_to_gif\` to convert ${serializeMediaReference(params.nodeId)} to an animated GIF.`,
        ...(params.duration !== undefined ? [`- duration: ${params.duration}`] : []),
      ];

    case 'videoSmartResize': {
      const { formats } = params;

      return [
        `Use \`reframe_video\` to reframe ${serializeMediaReference(params.nodeId)} for the following social media formats:`,
        ...formats.map((f) => {
          const target =
            'aspectRatio' in f ? `aspectRatio: ${f.aspectRatio}` : `width: ${f.width}, height: ${f.height}`;

          return `- ${f.platform}: ${f.format} (${target}, smartResizeFormatId: ${f.formatId})`;
        }),
        `Generate exactly ${formats.length} video${formats.length === 1 ? '' : 's'}, one per format.`,
      ];
    }

    case 'imageToVector':
      return [
        `Load the \`ai-editing\` skill, then use \`ai_image\` with the "vectorize" operation on ${serializeMediaReference(params.nodeId)}.`,
      ];

    case 'imageToVideo': {
      const count = params.variantCount ?? 1;

      if (count > 1) {
        return [
          `Load the \`ai-editing\` skill, then generate ${count} variants: call \`ai_video\` with the "imageToVideo" operation exactly ${count} times on ${serializeMediaReference(params.nodeId)}, same prompt each time.`,
          `- Prompt: "${params.prompt}"`,
          `Preserve the source image's aspect ratio in the generated videos.`,
          `Pass every returned URL in a single \`file_edit_complete\` call.`,
        ];
      }

      return [
        `Load the \`ai-editing\` skill, then use \`ai_video\` with the "imageToVideo" operation on ${serializeMediaReference(params.nodeId)}.`,
        `- Prompt: "${params.prompt}"`,
        `Preserve the source image's aspect ratio in the generated video.`,
      ];
    }
  }
};

export const buildTransformMessage = (params: TransformMessageParams): string => {
  return [
    ...getTransformationMessage(params),
    ...('nodeId' in params
      ? [`Only use ${serializeMediaReference(params.nodeId)} as the source media reference for this edit.`]
      : []),
  ].join('\n');
};

/** One message for several images: the agent allows a single run per thread, so targets cannot be sent separately. */
export const joinTransformMessages = (messages: readonly string[]): string =>
  messages.length === 1
    ? (messages[0] ?? '')
    : [
        "Run every edit below at the same time: in one step, start the tool calls for all of them together instead of waiting for one to finish before starting the next. Do not skip any or combine them. When all have finished, call `file_edit_complete` once, with every result in `edits`, ordered by edit number (all of Edit 1's results first).",
        ...messages.map((message, index) => `Edit ${index + 1}:\n${message}`),
      ].join('\n\n');

const buildExtendVideoMessage = ({
  direction,
  duration,
  nodeId,
}: {
  direction: string;
  duration: ExtendVideoDuration;
  nodeId: string;
}): string => {
  const sourceReference = serializeMediaReference(nodeId);

  return [
    `Extend ${sourceReference} by ${duration.seconds} seconds.`,
    direction,
    'Create exactly one extended video and leave the source unchanged. Return the normal extended output with the source video followed by its new continuation.',
    `Use only ${sourceReference} as the source video.`,
  ].join('\n');
};

export const buildExtendVideoPromptMessage = ({
  duration,
  nodeId,
  prompt,
}: {
  duration: ExtendVideoDuration;
  nodeId: string;
  prompt: string;
}): string =>
  buildExtendVideoMessage({
    direction: `Continue it with this direction: ${JSON.stringify(prompt)}`,
    duration,
    nodeId,
  });

export const buildExtendVideoSurpriseMeMessage = ({
  duration,
  nodeId,
}: {
  duration: ExtendVideoDuration;
  nodeId: string;
}): string => {
  return buildExtendVideoMessage({
    direction:
      'Choose a visually coherent and interesting continuation that suits the video. Do not ask follow-up questions.',
    duration,
    nodeId,
  });
};

/** Visible chat message for canvas social variants: sizes only — no format ids, skill names or counts. */
export const buildSocialVariantsMessage = ({
  formats,
  nodeId,
}: {
  formats: SmartResizeFormat[];
  nodeId: string;
}): string => {
  // Platform presets as "<Platform> <format> (W × H)", saved sizes by name with pixels, one-off sizes as bare pixels.
  const lines = formats.map(({ format, formatId, platform, savedSizeName }) => {
    const presetDimensions = getOutputDimensionsForFormat(formatId);

    if (presetDimensions) {
      return `${platform} ${format} (${getSizeDimensionsLabel(presetDimensions)})`;
    }

    const customDimensions = resolveExportDimensionsForFormat(formatId);

    if (customDimensions) {
      const pixelSize = getSizeDimensionsLabel(customDimensions);

      return savedSizeName ? `${savedSizeName} (${pixelSize})` : pixelSize;
    }

    return format;
  });

  return [
    `Generate social variants of ${serializeMediaReference(nodeId)} for:`,
    ...lines.map((line) => `• ${line}`),
  ].join('\n');
};
